import * as React from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';

import { cn } from '@/ux-common/lib/utils';

/**
 * Tooltip — thin registry wrappers around @radix-ui/react-tooltip with a CSS
 * class API.
 *
 * All visual styling lives in the theme (`themes/default/components/tooltip.css`
 * owns the API; `titan` rebinds the `--tooltip-*` vars to CKDS tokens). The React
 * layer only forwards refs, stamps `data-component`, merges `className` via `cn()`,
 * and applies the base class for each themable part.
 *
 * Radix requires the standard composition: a `TooltipProvider` somewhere up the
 * tree, then `Tooltip` (root) wrapping a `TooltipTrigger` and a portaled
 * `TooltipContent`. `TooltipArrow` is optional and, when rendered inside
 * `TooltipContent`, inherits the bubble's background fill.
 *
 * **Class API** (owned by `theme-default`, restyled by `titan`):
 * - `.tooltip-content` — the floating bubble (bg, radius, padding, shadow, `z-tooltip`, animations)
 * - `.tooltip-content-inverse` — modifier: light-surface variant
 * - `.tooltip-arrow` — the pointer; fill tracks the content background
 *
 * The trigger carries no forced base class so it composes cleanly with Radix's
 * `asChild` (the class/attrs merge onto the child element).
 *
 * @example
 * ```tsx
 * <TooltipProvider>
 *   <Tooltip>
 *     <TooltipTrigger asChild>
 *       <Button aria-label="Info">?</Button>
 *     </TooltipTrigger>
 *     <TooltipPortal>
 *       <TooltipContent>
 *         Threshold exceeded
 *         <TooltipArrow />
 *       </TooltipContent>
 *     </TooltipPortal>
 *   </Tooltip>
 * </TooltipProvider>
 *
 * // Light-surface variant — size/side are Radix props; the look is a class
 * <TooltipContent className="tooltip-content-inverse" side="right">…</TooltipContent>
 * ```
 */

/**
 * The tooltip half of a wrapper component's prop surface.
 *
 * `withTooltip` and `DisabledTooltip` differ in what they wrap and agree entirely on
 * the tooltip they produce, so this is the shared half. Declaring it twice is what
 * let the two drift apart inside a single change — they ended up with different
 * portal guards and different names for the content prop.
 *
 * Positioning is prefixed because `withTooltip` CONSUMES every name it declares: an
 * unprefixed `side` would be swallowed instead of reaching a wrapped component that
 * has its own. `DisabledTooltip` spreads nothing onto a child and could not collide,
 * but matches so the two read as one API.
 *
 * Every property is optional and passes `undefined` straight through, so omitting
 * one lands on Radix's own default rather than a value a wrapper invented.
 */
export interface TooltipPositioningProps {
  /** Class merged onto `TooltipContent` — e.g. `tooltip-content-inverse`. */
  contentClassName?: string;
  /** Per-instance open delay. Overrides the provider for this tooltip only. */
  delayDuration?: number;
  /** Radix placement side. Default: `'top'`. */
  tooltipSide?: 'top' | 'right' | 'bottom' | 'left';
  /** Radix alignment along the chosen side. Default: `'center'`. */
  tooltipAlign?: 'start' | 'center' | 'end';
  /** Distance in px from the trigger. Default: `4` (the registry `TooltipContent` default). */
  tooltipSideOffset?: number;
  /**
   * Offset in px along the alignment axis. Default: `0` (Radix).
   *
   * Only applies when `tooltipAlign` is `'start'` or `'end'` — floating-ui ignores it
   * for the default centre alignment.
   */
  tooltipAlignOffset?: number;
  /**
   * Draws the registry `TooltipArrow` on the bubble, pointing at the trigger. Off by
   * default, so turning it on is a visible change a call site opts into.
   *
   * The fill follows the bubble — `--tooltip-bg-default`, or `--tooltip-bg-inverse`
   * under `tooltip-content-inverse` — so it needs no colour of its own. A
   * `contentClassName` that repaints the bubble some other way leaves the arrow
   * behind. Radix adds the arrow's height to `tooltipSideOffset`, so the arrow's
   * tip, not the bubble, sits that far from the trigger.
   */
  tooltipArrow?: boolean;
  /**
   * Stops `click`, `pointerup` and `mouseup` raised inside the bubble from reaching an
   * ancestor's handler. Off by default, so a plain label tooltip is unaffected.
   *
   * Those three and no others. `pointerdown`/`mousedown` still propagate **by
   * design** — stopping them breaks outside-dismissal on any ancestor Radix layer and
   * on consumers' own click-outside hooks (see the note in `TooltipShell`). So an
   * ancestor's `onMouseDown`/`onPointerDown` still fires; only the commit-style events
   * are suppressed. `contextmenu`, `dblclick` and focus/keyboard events also
   * propagate.
   *
   * `pointerup` and `mouseup` are in the set because the common ancestors commit
   * there rather than on `click`: Radix's `DropdownMenu.Item` and `Select.Item` both
   * select on `pointerup`, and a text-selection drag ends in a `mouseup`.
   *
   * `TooltipContent` is portaled to `document.body`, which stops DOM bubbling but
   * NOT React's: a synthetic event propagates through the React tree, so a click on
   * the bubble still reaches the `onClick` of whatever renders the tooltip — a
   * clickable nav row, a selectable card. Turn this on when the bubble carries
   * content a user is meant to point at, so interacting with it does not also
   * activate the thing it describes.
   *
   * Not a licence to put focusable controls in a tooltip: a tooltip is not reachable
   * by keyboard, so anything interactive inside it is unreachable too. Use a Popover
   * for that. This is for text a user may want to hover or select.
   */
  interactiveContent?: boolean;
}

export type TooltipProviderProps = React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Provider>;

/**
 * TooltipProvider — Radix Provider (delay/skip config). Renders no DOM of its own.
 *
 * Mount ONE near the app root. It is app-level policy: it owns the hover delay and
 * the skip-delay group that lets a second tooltip open instantly after the first.
 * Registry components deliberately mount none of their own, and Radix throws without
 * one somewhere above the tooltip.
 *
 * **`delayDuration` defaults to `0`, not Radix's 700ms.** A tooltip here carries
 * supplemental information the user has already asked for by pointing at the
 * trigger, and 700ms reads as the UI failing to respond — long enough that people
 * move the pointer away before it appears. Prometheus already set `0` app-wide for
 * exactly this reason, so the default now matches what consumers were configuring
 * by hand. Pass `delayDuration` explicitly to get any other value, including 700.
 *
 * Per-tooltip overrides still work: `Tooltip` (the root) takes its own
 * `delayDuration` and Radix resolves it as `prop ?? provider`, so one slow tooltip
 * does not require changing the app default.
 */
export const TooltipProvider = ({ delayDuration = 0, ...props }: TooltipProviderProps) => (
  <TooltipPrimitive.Provider delayDuration={delayDuration} {...props} />
);
TooltipProvider.displayName = 'TooltipProvider';

/** Tooltip — Radix Root. Owns open/close state for a single tooltip. */
export const Tooltip = TooltipPrimitive.Root;

/** TooltipPortal — Radix Portal. Renders content into `document.body` by default. */
export const TooltipPortal = TooltipPrimitive.Portal;

/**
 * TooltipShell — the shell both wrapper components render: a trigger, and a portaled
 * bubble when there is something to say.
 *
 * A component rather than a `renderShell()` helper for a vendoring reason, not a
 * stylistic one. A non-component export in a component module trips
 * `react-refresh/only-export-components`, which is off here but ON in consumers — and
 * the registry is VENDORED, so the warning lands in their repo, in a file they are
 * told not to hand-edit. `input.constants.ts` exists for exactly this. It also always
 * WAS a component: it takes props and returns JSX; it was only ever named like a
 * helper.
 *
 * Shared because `withTooltip` and `DisabledTooltip` differ ONLY in what they wrap —
 * a component via `asChild`, or a focusable span around a disabled control — and
 * agree on everything downstream of that. Copies of this block drifted twice inside
 * one PR: once on the portal guard (`&&` vs a ternary, which renders a stray "0" for
 * numeric content) and once on how the dangling-describedby case was handled. Two
 * modules owning one behavioural contract is what produced both.
 *
 * Three details here are load-bearing and easy to undo by accident:
 *
 * 1. **Every positioning prop passes `undefined` straight through.** That is what
 *    makes Radix's own defaults apply rather than a value a wrapper invented, and it
 *    is what keeps the HOC drop-in for the legacy one. Do not add `?? something`.
 * 2. **`aria-describedby` is suppressed when there is no content.** Radix stamps the
 *    trigger from the ROOT's open state alone, and the root still opens with no
 *    portal — so without this it points at an id that never mounted. The trigger
 *    spreads caller props after Radix's own, so passing the attribute explicitly
 *    wins. The root's own open path still runs; that is Radix behaviour, tracked
 *    separately.
 * 3. **The trigger renders OUTSIDE the branch that toggles the portal.** That is what
 *    lets a caller gate a tooltip with `content={undefined}` instead of a `disabled`
 *    flag: the trigger keeps its slot in the fragment, so React reconciles the
 *    wrapped element in place and an effect measuring it survives. Moving the trigger
 *    inside the conditional would remount it on every toggle.
 */
export interface TooltipShellProps extends TooltipPositioningProps {
  /** Bubble contents. Falsy renders no portal and suppresses the description. */
  content?: React.ReactNode;
  /** Rendered inside `TooltipTrigger asChild`. */
  trigger: React.ReactNode;
}

export const TooltipShell = ({
  content,
  trigger,
  contentClassName,
  tooltipSide,
  tooltipAlign,
  tooltipSideOffset,
  tooltipAlignOffset,
  tooltipArrow,
  interactiveContent,
}: TooltipShellProps) => {
  // `stopPropagation` on the React synthetic event is what matters: the portal
  // already prevents DOM bubbling, but React routes synthetic events through the
  // component tree regardless of where the node lives.
  //
  // The `*down` pair is deliberately NOT stopped, and that is load-bearing. React's
  // listeners for a portal sit on `document.body`, so stopping a synthetic event also
  // stops the native one before it reaches `document` — and Radix's `DismissableLayer`
  // depends on a document-level, bubble-phase `pointerdown` listener for BOTH halves
  // of its outside-press logic: it dismisses there, and it resets the
  // `isPointerInsideReactTree` flag there (dismissable-layer dist:142-163). Stopping
  // `pointerdown` leaves that flag stuck true, so an ancestor Popover/Dialog/Menu
  // ignores the NEXT outside click entirely. Consumers' own click-outside hooks break
  // the same way.
  //
  // Stopping `click` is safe against that primitive: its document-level `click`
  // listener is registered only for `pointerType === 'touch'`, and only for a press
  // that was already outside this bubble (dist:154-156).
  //
  // The cost of the narrower set is that an ancestor's own `onMouseDown` /
  // `onPointerDown` still fires. That is the right trade: a leaked `*down` handler is
  // a nuisance, a layer that will not dismiss is a trap.
  const stopPropagation = interactiveContent
    ? (event: React.SyntheticEvent) => event.stopPropagation()
    : undefined;

  return (
    <>
      <TooltipTrigger asChild {...(content ? null : { 'aria-describedby': undefined })}>
        {trigger}
      </TooltipTrigger>
      {/* A ternary rather than `&&`: `content={0}` would make `&&` evaluate to `0`,
          which React renders as a stray "0" text node inside the Tooltip root. */}
      {content ? (
        <TooltipPortal>
          <TooltipContent
            className={contentClassName}
            side={tooltipSide}
            align={tooltipAlign}
            sideOffset={tooltipSideOffset}
            alignOffset={tooltipAlignOffset}
            onClick={stopPropagation}
            onPointerUp={stopPropagation}
            onMouseUp={stopPropagation}
          >
            {content}
            {tooltipArrow ? <TooltipArrow /> : null}
          </TooltipContent>
        </TooltipPortal>
      ) : null}
    </>
  );
};
TooltipShell.displayName = 'TooltipShell';

export type TooltipTriggerProps = React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Trigger>;

/**
 * TooltipTrigger — the anchor. Forwards ref, stamps `data-component`, and passes
 * `className` through unchanged (no forced base class, so `asChild` composition is
 * clean). Use `asChild` to make an existing control the trigger.
 */
export const TooltipTrigger = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Trigger>,
  TooltipTriggerProps
>(({ className, asChild, type, ...props }, ref) => (
  <TooltipPrimitive.Trigger
    ref={ref}
    asChild={asChild}
    // Radix renders a bare `Primitive.button`, and a <button> with no `type`
    // defaults to `submit` — so a trigger inside a <form> would post it on click.
    // `button.tsx` defaults `type="button"` for the same reason. Skipped under
    // `asChild`, where Radix merges props onto the caller's element and the
    // attribute could land on an <a> or <div>. There `type` is forwarded only
    // when set: Slot lets these props win, so an explicit `type: undefined`
    // would erase the child's own `type="button"` and make it a submit button.
    {...(asChild ? type !== undefined && { type } : { type: type ?? 'button' })}
    data-component="TooltipTrigger"
    className={className}
    {...props}
  />
));
TooltipTrigger.displayName = TooltipPrimitive.Trigger.displayName;

export type TooltipContentProps = React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>;

/**
 * TooltipContent — the floating bubble. Applies the `.tooltip-content` base class
 * (compose `.tooltip-content-inverse` via `className` for the light variant),
 * defaults `sideOffset` to 4, forwards ref, and stamps `data-component`.
 */
export const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  TooltipContentProps
>(({ className, sideOffset = 4, ...props }, ref) => (
  <TooltipPrimitive.Content
    ref={ref}
    sideOffset={sideOffset}
    data-component="TooltipContent"
    className={cn('tooltip-content', className)}
    {...props}
  />
));
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

export type TooltipArrowProps = React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Arrow>;

/**
 * TooltipArrow — optional pointer rendered inside `TooltipContent`. Applies the
 * `.tooltip-arrow` base class (fill tracks the content background); forwards ref
 * and stamps `data-component`.
 */
export const TooltipArrow = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Arrow>,
  TooltipArrowProps
>(({ className, ...props }, ref) => (
  <TooltipPrimitive.Arrow
    ref={ref}
    data-component="TooltipArrow"
    className={cn('tooltip-arrow', className)}
    {...props}
  />
));
TooltipArrow.displayName = TooltipPrimitive.Arrow.displayName;
